import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Tooltip,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Science as ScienceIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  PlayCircle as InProgressIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  GetApp as GetAppIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  TableChart,
  PictureAsPdf,
} from '@mui/icons-material';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { Popover, Skeleton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { fr } from 'date-fns/locale';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';
import JSZip from 'jszip';
import { clientDashboardService } from '../services/clientDashboardService';
import apiService from '../services/api';
import { generateProfessionalExcel, exportToPremiumPDF, loadExcelJSLibrary } from '../utils/premiumCertificateExport';
import ClientWorkflowVisualization from '../components/ClientWorkflowVisualization';
import ClientPortalEnhanced from '../components/ClientPortalEnhanced';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  BarElement
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      style={{
        width: '100%',
        overflow: 'hidden',
        WebkitOverflowScrolling: 'touch'
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: { xs: 2, md: 3 },
          width: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          maxHeight: 'calc(100vh - 200px)',
          boxSizing: 'border-box'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClientDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State management
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>({});
  const [samples, setSamples] = useState<any[]>([]);
  const [certificates, setCertificates] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [samplePage, setSamplePage] = useState(0);
  const [sampleRowsPerPage, setSampleRowsPerPage] = useState(25); // Better for large datasets
  const [certificatePage, setCertificatePage] = useState(0);
  const [certificateRowsPerPage, setCertificateRowsPerPage] = useState(50); // Better for large datasets
  const [sampleFilters, setSampleFilters] = useState({
    status: '',
    priority: '',
    search: '',
  });
  const [searchDebounceTimeout, setSearchDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [sampleTracking, setSampleTracking] = useState<any>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'warning' | 'info',
  });
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [certificateTotal, setCertificateTotal] = useState(0);
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  const [certificateFilters, setCertificateFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [certificateSearchDebounceTimeout, setCertificateSearchDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const [datePickerLabel, setDatePickerLabel] = useState('Période');
  const [tempDateFrom, setTempDateFrom] = useState<Date | null>(null);
  const [tempDateTo, setTempDateTo] = useState<Date | null>(null);
  const [certificateSearchInput, setCertificateSearchInput] = useState('');
  const [bulkExporting, setBulkExporting] = useState(false);
  const [bulkExportProgress, setBulkExportProgress] = useState({ current: 0, total: 0 });

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      const data = await clientDashboardService.getDashboardOverview();
      setDashboardData(data);
      setAnalytics(data.analytics || {});
    } catch (error) {
      setSnackbar({ open: true, message: 'Erreur lors du chargement du tableau de bord', severity: 'error' });
    }
  }, []);

  // Toggle batch expansion
  const toggleBatchExpansion = (batchNumber: string) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchNumber)) {
        newSet.delete(batchNumber);
      } else {
        newSet.add(batchNumber);
      }
      return newSet;
    });
  };



  // Debounced search for better performance with large datasets
  const handleSearchChange = useCallback((searchTerm: string) => {
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout);
    }
    
    const timeout = setTimeout(() => {
      setSampleFilters(prev => ({ ...prev, search: searchTerm }));
      setSamplePage(0); // Reset to first page on search
    }, 500); // 500ms debounce
    
    setSearchDebounceTimeout(timeout);
  }, [searchDebounceTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout);
      }
      if (certificateSearchDebounceTimeout) {
        clearTimeout(certificateSearchDebounceTimeout);
      }
    };
  }, [searchDebounceTimeout, certificateSearchDebounceTimeout]);

  // Debounced search for certificates (min 3 chars to avoid expensive scans)
  const handleCertificateSearchChange = useCallback((searchTerm: string) => {
    if (certificateSearchDebounceTimeout) {
      clearTimeout(certificateSearchDebounceTimeout);
    }
    
    const timeout = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if (trimmed.length >= 3 || trimmed.length === 0) {
        setCertificateFilters(prev => ({ ...prev, search: trimmed }));
        setCertificatePage(0);
      }
    }, 500);
    
    setCertificateSearchDebounceTimeout(timeout);
  }, [certificateSearchDebounceTimeout]);

  // Handle sample location view
  const handleSampleLocation = (sampleId: string, sampleData: any) => {
    setSnackbar({ 
      open: true, 
      message: `Localisation pour échantillon ${sampleData.sample_id}: ${sampleData.location || 'Position en cours de mise à jour'}`, 
      severity: 'info' 
    });
  };

  // Handle detailed sample tracking
  const handleDetailedSampleTracking = (sampleId: string, sampleData: any) => {
    setSnackbar({ 
      open: true, 
      message: `Suivi détaillé pour ${sampleData.sample_id} - Statut: ${getStatusLabel(sampleData.statut)}`, 
      severity: 'info' 
    });
    // Open the existing tracking dialog
    handleSampleTracking(sampleId);
  };

  // Stabilize sampleFilters object to prevent infinite loops
  const stableSampleFilters = useMemo(() => sampleFilters, [
    sampleFilters.status,
    sampleFilters.priority,
    sampleFilters.search
  ]);

  const loadSamples = useCallback(async () => {
    try {
      const params = {
        page: samplePage + 1,
        limit: sampleRowsPerPage,
        ...stableSampleFilters,
      };
      const data = await clientDashboardService.getSamples(params);
      setSamples(data.samples || []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Erreur lors du chargement des échantillons', severity: 'error' });
    }
  }, [samplePage, sampleRowsPerPage, stableSampleFilters]);

  // Stabilize certificateFilters object to prevent infinite loops
  const stableCertificateFilters = useMemo(() => certificateFilters, [
    certificateFilters.search,
    certificateFilters.dateFrom,
    certificateFilters.dateTo
  ]);

  const loadCertificates = useCallback(async () => {
    setCertificatesLoading(true);
    try {
      const params = {
        page: certificatePage + 1,
        limit: certificateRowsPerPage,
        ...stableCertificateFilters,
      };
      const data = await clientDashboardService.getCertificates(params);
      setCertificates(data.certificates || []);
      setCertificateTotal(data.total || 0);
    } catch (error) {
      setSnackbar({ open: true, message: 'Erreur lors du chargement des certificats', severity: 'error' });
    } finally {
      setCertificatesLoading(false);
    }
  }, [certificatePage, certificateRowsPerPage, stableCertificateFilters]);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await clientDashboardService.getNotifications();
      setNotifications(data.notifications || []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Erreur lors du chargement des notifications', severity: 'error' });
    }
  }, []);

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadDashboardData(),
        loadSamples(),
        loadCertificates(),
        loadNotifications(),
      ]);
      setSnackbar({ open: true, message: 'Données actualisées avec succès', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Échec de l\'actualisation des données', severity: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSampleTracking = async (sampleId: string) => {
    setTrackingLoading(true);
    try {
      const tracking = await clientDashboardService.getSampleTracking(sampleId);
      setSampleTracking(tracking);
      setTrackingDialogOpen(true);
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Échec du chargement des données de suivi', severity: 'error' });
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleCertificateDownload = async (certificateId: string) => {
    try {
      await clientDashboardService.downloadCertificateHTML(certificateId);
      setSnackbar({ open: true, message: 'Téléchargement du certificat démarré', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Échec du téléchargement du certificat', severity: 'error' });
    }
  };

  const handleCertificateView = async (certificateId: string) => {
    try {
      const certData = await clientDashboardService.viewCertificate(certificateId);
      // Open certificate in new window for viewing
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(certData.html_content);
        newWindow.document.title = `Certificat ${certData.certificate_number}`;
      }
      setSnackbar({ open: true, message: 'Certificat ouvert pour visualisation', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Échec de l\'ouverture du certificat', severity: 'error' });
    }
  };

  const handleCertificateExport = async () => {
    try {
      const blob = await clientDashboardService.downloadAllCertificatesExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificats_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Export CSV des certificats réussi', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Erreur lors de l\'export des certificats', severity: 'error' });
    }
  };

  const handleCertificateExportExcel = async () => {
    try {
      // Ensure ExcelJS is loaded
      if (!(window as any).ExcelJS) {
        await loadExcelJSLibrary();
      }
      const ExcelJS = (window as any).ExcelJS;

      // Fetch certificates for export (limit to prevent timeouts)
      const maxExportLimit = 500; // Reasonable limit for Excel export
      const all = await clientDashboardService.getClientCertificates(1, maxExportLimit);
      const rows = all.certificates || [];

      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Certificats');

      // Set worksheet properties with larger cells for better readability
      worksheet.properties.defaultRowHeight = 30;
      worksheet.properties.defaultColWidth = 20;

      // Header row with larger styling
      const header = ['Numéro', 'Échantillon', 'Réf. Client', 'Projet', 'Date Génération', 'Statut'];
      const headerRow = worksheet.addRow(header);
      headerRow.height = 40; // Increased header height
      headerRow.eachCell((cell: any) => {
        cell.font = { bold: true, size: 14 }; // Increased font size
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A90D9' } }; // Better color
        cell.font.color = { argb: 'FFFFFFFF' }; // White text
        cell.border = { 
          top: { style: 'medium' }, 
          bottom: { style: 'medium' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Data rows with improved styling
      rows.forEach((cert: any, index: number) => {
        const dataRow = worksheet.addRow([
          cert.certificate_number || '',
          cert.sample_id || '',
          cert.sample_external_id || '',
          cert.project_name || '',
          cert.generated_at ? new Date(cert.generated_at).toLocaleString('fr-FR') : '',
          cert.status || ''
        ]);
        dataRow.height = 28; // Increased row height
        
        // Alternating row colors
        const isEvenRow = index % 2 === 0;
        const rowBgColor = isEvenRow ? 'FFFFFFFF' : 'FFF8F9FA';
        
        dataRow.eachCell((cell: any) => {
          cell.font = { size: 12 }; // Increased font size
          cell.alignment = { vertical: 'middle', horizontal: 'center', indent: 0.5 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBgColor } };
          cell.border = { 
            top: { style: 'thin', color: { argb: 'FFE1E5E9' } },
            bottom: { style: 'thin', color: { argb: 'FFE1E5E9' } },
            left: { style: 'thin', color: { argb: 'FFE1E5E9' } },
            right: { style: 'thin', color: { argb: 'FFE1E5E9' } }
          };
        });
      });

      // Better column sizing
      worksheet.columns.forEach((col: any, index: number) => {
        let max = 10;
        col.eachCell({ includeEmpty: true }, (cell: any) => {
          const v = cell.value ? cell.value.toString() : '';
          if (v.length > max) max = v.length;
        });
        // Larger minimum and maximum widths
        const minWidth = 15;
        const maxWidth = 45;
        col.width = Math.min(Math.max(max + 4, minWidth), maxWidth); // Added extra padding
      });

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Mes_Certificats_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Export Excel des certificats réussi', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Erreur lors de l\'export Excel des certificats', severity: 'error' });
    }
  };

  // Per-certificate actions using the EXACT same premium functions as admin
  const handleCertificatePDF = async (certificateId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setSnackbar({ open: true, message: 'Token manquant', severity: 'error' });
        return;
      }
      
      const cert = await clientDashboardService.viewCertificate(certificateId);
      await exportToPremiumPDF(certificateId, cert.certificate_number, token);
      setSnackbar({ open: true, message: '🏆 Fenêtre PDF Premium ouverte - Utilisez Ctrl+P pour sauvegarder', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: '🚨 Erreur lors de l\'export PDF Premium', severity: 'error' });
    }
  };

  const handleCertificateExcel = async (certificateId: string, displayName?: string) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/api/v1/certificates/${certificateId}/data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des données du certificat');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Erreur lors de la récupération des données');
      }
      
      const filename = displayName || result.data.certificate_number;
      await generateProfessionalExcelLocal(result.data, filename);
      
      setSnackbar({ open: true, message: '🏆 Export Excel Premium réussi', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: '🚨 Erreur lors de l\'export Excel Premium', severity: 'error' });
    }
  };

  const dateShortcuts = [
    { label: "Aujourd'hui", getRange: () => { const d = new Date(); return [startOfDay(d), endOfDay(d)]; } },
    { label: 'Hier', getRange: () => { const d = subDays(new Date(), 1); return [startOfDay(d), endOfDay(d)]; } },
    { label: 'Cette semaine', getRange: () => { const d = new Date(); return [startOfWeek(d, { weekStartsOn: 1 }), endOfWeek(d, { weekStartsOn: 1 })]; } },
    { label: 'Ce mois', getRange: () => { const d = new Date(); return [startOfMonth(d), endOfMonth(d)]; } },
    { label: 'Mois dernier', getRange: () => { const d = subMonths(new Date(), 1); return [startOfMonth(d), endOfMonth(d)]; } },
    { label: '3 derniers mois', getRange: () => [startOfMonth(subMonths(new Date(), 2)), endOfDay(new Date())] },
  ];

  const applyDateRange = (from: Date | null, to: Date | null, label?: string) => {
    setCertificateFilters(prev => ({
      ...prev,
      dateFrom: from ? format(from, 'yyyy-MM-dd') : '',
      dateTo: to ? format(to, 'yyyy-MM-dd') : '',
    }));
    setDatePickerLabel(label || (from && to ? `${format(from, 'dd/MM/yy')} - ${format(to, 'dd/MM/yy')}` : 'Période'));
    setCertificatePage(0);
    setDatePickerAnchor(null);
  };

  const generateExcelBuffer = async (certificateId: string): Promise<ArrayBuffer | null> => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/certificates/${certificateId}/data`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) return null;
      const result = await response.json();
      if (!result.success) return null;

      const buffer = await generateProfessionalExcelLocal(result.data, result.data.certificate_number, true);
      return (buffer as ArrayBuffer) || null;
    } catch {
      return null;
    }
  };

  const handleBulkExcelExport = async () => {
    if (!certificateFilters.dateFrom || !certificateFilters.dateTo) {
      setSnackbar({ open: true, message: 'Veuillez sélectionner une période pour l\'export groupé', severity: 'warning' });
      return;
    }
    setBulkExporting(true);
    try {
      const allData = await clientDashboardService.getCertificates({
        page: 1, limit: 9999,
        dateFrom: certificateFilters.dateFrom, dateTo: certificateFilters.dateTo,
      });
      const certs = allData.certificates || [];
      if (certs.length === 0) {
        setSnackbar({ open: true, message: 'Aucun certificat dans cette période', severity: 'warning' });
        return;
      }

      setBulkExportProgress({ current: 0, total: certs.length });
      const zip = new JSZip();
      const concurrency = 5;

      for (let i = 0; i < certs.length; i += concurrency) {
        const batch = certs.slice(i, i + concurrency);
        const results = await Promise.all(batch.map(async (cert: any) => {
          const buffer = await generateExcelBuffer(cert.id);
          const safeName = (cert.display_name || cert.certificate_number || cert.id).replace(/[/\\?%*:|"<>]/g, '_');
          return { name: `${safeName}.xlsx`, buffer };
        }));
        results.forEach(r => { if (r.buffer) zip.file(r.name, r.buffer); });
        setBulkExportProgress({ current: Math.min(i + concurrency, certs.length), total: certs.length });
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificats_${certificateFilters.dateFrom}_${certificateFilters.dateTo}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: `Export de ${certs.length} certificats réussi`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erreur lors de l\'export groupé', severity: 'error' });
    } finally {
      setBulkExporting(false);
      setBulkExportProgress({ current: 0, total: 0 });
    }
  };

  // Helper function to check if certificate has QA/QC data
  const hasQAQCData = (certificateData: any): boolean => {
    if (certificateData.certificate_type !== 'BATCH') return false;
    if (!certificateData.samples || certificateData.samples.length === 0) return false;
    
    const firstSample = certificateData.samples[0];
    const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
    
    const isBGM = metadonnees?.bgm_method_selected === true;
    const qaqcData = metadonnees?.qaqc_data;
    const hasData = qaqcData?.reference_standards && 
                    Array.isArray(qaqcData.reference_standards) && 
                    qaqcData.reference_standards.length > 0;
    
    // 🔧 ZMSM/non-BGM support: gate on data presence only.
    // Mirror of the deployed surgical patch — backend already gates by data only too.
    void isBGM; // intentionally unused (kept for type-safety / readability)
    return hasData;
  };

  // Generate QA/QC worksheet for BGM certificates
  const generateQAQCWorksheet = (workbook: any, certificateData: any, ExcelJS: any) => {
    const firstSample = certificateData.samples[0];
    const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
    const qaqcData = metadonnees?.qaqc_data;
    
    if (!qaqcData?.reference_standards) return;
    
    const qaqcSheet = workbook.addWorksheet('QAQC');
    
    // 🔧 ZMSM/non-BGM support: when the cert is BGM, keep the fixed 10-element header
    //   (byte-for-byte unchanged). When non-BGM, derive columns from the actual
    //   reference_standards data so ZMSM (5 elems + optional Au) renders correctly.
    //   Mirror of the deployed surgical patch.
    const isBGMQAQC = (metadonnees as any)?.bgm_method_selected === true;
    const _refRow0: Record<string, any> = (qaqcData.reference_standards && qaqcData.reference_standards[0]) || {};
    const _defUnits: Record<string, string> = {
      Ag: 'g/t', Au: 'g/t', Au_FA: 'g/t', Ag_FA: 'g/t',
      Cu: '%', Fe: '%', Pb: '%', Zn: '%',
      Mo_ICP: 'ppm', As_ICP: 'ppm', Sn_ICP: 'ppm',
      Cd: 'ppm', Co: 'ppm', Mn: 'ppm', Ni: 'ppm', H2O: '%',
    };
    const _defMeth: Record<string, string> = {
      Ag: 'AA-PRE.MO/ANA/002', Au: 'AA-PRE.MO/ANA/001',
      Cu: 'AA-PRE.MO/ANA/002', Fe: 'AA-PRE.MO/ANA/002',
      Pb: 'AA-PRE.MO/ANA/002', Zn: 'AA-PRE.MO/ANA/002',
      Ag_FA: 'MO/ANA/035', Au_FA: 'MO/ANA/001',
      Mo_ICP: 'MO/ANA/038', As_ICP: 'MO/ANA/038', Sn_ICP: 'MO/ANA/038',
    };
    const _defLod: Record<string, string> = {
      Ag: '1', Au: '0,05', Cu: '0,01', Fe: '0,01', Pb: '0,01', Zn: '0,01',
      Ag_FA: '200', Au_FA: '0,05', Mo_ICP: '1', As_ICP: '1', Sn_ICP: '1',
    };
    const allElements: string[] = isBGMQAQC
      ? ['Ag', 'Cu', 'Fe', 'Pb', 'Zn', 'Ag_FA', 'Au_FA', 'Mo_ICP', 'As_ICP', 'Sn_ICP']
      : Object.keys(_refRow0).filter(k => k !== 'customers_ref' && k !== 'lab_ref').sort();
    const elementUnits: Record<string, string> = isBGMQAQC
      ? {
          'Ag': 'g/t', 'Cu': '%', 'Fe': '%', 'Pb': '%', 'Zn': '%',
          'Ag_FA': 'g/t', 'Au_FA': 'g/t',
          'Mo_ICP': 'ppm', 'As_ICP': 'ppm', 'Sn_ICP': 'ppm'
        }
      : allElements.reduce((o: Record<string, string>, k) => { o[k] = _defUnits[k] || 'ppm'; return o; }, {});
    const methodHeaders: string[] = isBGMQAQC
      ? ['MO/ANA/002', 'MO/ANA/002', 'MO/ANA/002', 'MO/ANA/002', 'MO/ANA/002',
         'MO/ANA/035', 'MO/ANA/001', 'MO/ANA/038', 'MO/ANA/038', 'MO/ANA/038']
      : allElements.map(k => _defMeth[k] || '-');
    const limitHeaders: string[] = isBGMQAQC
      ? ['1', '0,01', '0,01', '0,01', '0,01', '200', '0,05', '1', '1', '1']
      : allElements.map(k => _defLod[k] || '');
    
    const headerStyle = {
      font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8CCE4' } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: {
        top: { style: 'thin', color: { argb: 'FF555555' } },
        bottom: { style: 'thin', color: { argb: 'FF555555' } },
        left: { style: 'thin', color: { argb: 'FF555555' } },
        right: { style: 'thin', color: { argb: 'FF555555' } }
      }
    };
    
    const dataStyle = {
      font: { size: 10, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
      border: {
        top: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        left: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        right: { style: 'hair', color: { argb: 'FFDDDDDD' } }
      }
    };
    
    const emptyDataStyle = {
      ...dataStyle,
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } }
    };
    
    qaqcSheet.mergeCells('A1:L1');
    qaqcSheet.getCell('A1').value = '2. QA/QC Results';
    qaqcSheet.getCell('A1').style = {
      font: { bold: true, size: 14, color: { argb: 'FF000000' }, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' }
    };
    qaqcSheet.getRow(1).height = 25;
    
    qaqcSheet.mergeCells('A3:B3');
    qaqcSheet.getCell('A3').value = 'METHOD';
    qaqcSheet.getCell('A3').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(3, idx + 3);
      cell.value = methodHeaders[idx];
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(3).height = 25;
    
    qaqcSheet.mergeCells('A4:B4');
    qaqcSheet.getCell('A4').value = 'LIMITE DETECTION';
    qaqcSheet.getCell('A4').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(4, idx + 3);
      cell.value = limitHeaders[idx];
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(4).height = 25;
    
    qaqcSheet.getCell('A5').value = 'Customers-Ref';
    qaqcSheet.getCell('A5').style = headerStyle;
    qaqcSheet.getCell('B5').value = 'Lab-Ref';
    qaqcSheet.getCell('B5').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(5, idx + 3);
      cell.value = `${el}\n${elementUnits[el]}`;
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(5).height = 30;
    
    qaqcData.reference_standards.forEach((standard: any, rowIdx: number) => {
      const rowNum = 6 + rowIdx;
      
      const custCell = qaqcSheet.getCell(rowNum, 1);
      custCell.value = standard.customers_ref || '';
      custCell.style = (!standard.customers_ref || standard.customers_ref.trim() === '') 
                       ? emptyDataStyle : dataStyle;
      
      const labCell = qaqcSheet.getCell(rowNum, 2);
      labCell.value = standard.lab_ref || '';
      labCell.style = (!standard.lab_ref || standard.lab_ref.trim() === '') 
                      ? emptyDataStyle : dataStyle;
      
      allElements.forEach((el, colIdx) => {
        const cell = qaqcSheet.getCell(rowNum, colIdx + 3);
        const value = standard[el];
        cell.value = value || '';
        cell.style = (!value || value.trim() === '') ? emptyDataStyle : dataStyle;
      });
      
      qaqcSheet.getRow(rowNum).height = 22;
    });
    
    const footerRow = 6 + qaqcData.reference_standards.length + 1;
    qaqcSheet.mergeCells(`A${footerRow}:L${footerRow}`);
    const footerCell = qaqcSheet.getCell(`A${footerRow}`);
    const analystName = qaqcData.batch_info?.analyst_name || 'N/A';
    const analysisDate = qaqcData.batch_info?.analysis_date 
      ? new Date(qaqcData.batch_info.analysis_date).toLocaleDateString('en-GB') 
      : 'N/A';
    footerCell.value = `* QA/QC data validated by: ${analystName} on ${analysisDate}`;
    footerCell.style = {
      font: { italic: true, size: 9, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' }
    };
    
    qaqcSheet.columns = [
      { width: 25 }, { width: 20 },
      { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 },
      { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }
    ];
  };

  const resolveExcelMainSheetName = (certificateData: any): string => {
  const codes = new Set<string>();
  const addCode = (m: unknown) => {
    if (m != null && String(m).trim()) codes.add(String(m));
  };
  (certificateData.samples || []).forEach((sample: any) => {
    const meta = sample.sample_data?.metadonnees || sample.metadonnees || {};
    if (meta.bgm_method_selected) codes.add('BGM-MULTI-LAB');
    (meta.analyses_demandees || []).forEach(addCode);
    Object.values(meta.element_method_mapping || {}).forEach(addCode);
    (sample.results || []).forEach((r: any) => addCode(r.method_code));
  });
  (certificateData.results || []).forEach((r: any) => addCode(r.method_code));
  const list = Array.from(codes);
  if (list.some((m) => /BGM-MULTI-LAB/i.test(m))) return 'Rapport AA';
  if (list.some((m) => /ICP|D4A|FP-ICP|PRE\.MO\/ANA\/038|PRE\.MO\/ANA\/037|PRE\.MO\/ANA\/039|PRE\.MO\/ANA\/040|PRE\.MO\/ANA\/041|PRE\.MO\/ANA\/007/.test(m))) {
    return 'Rapport ICP';
  }
  if (list.some((m) => /ANA\/002|SAA|AA-PRE\.MO/i.test(m))) return 'Rapport SAA';
  return 'Rapport AA';
};

  // EXACT COPY of generateProfessionalExcel from admin CertificatesNew.tsx
  const generateProfessionalExcelLocal = async (certificateData: any, certificateNumber: string, returnBuffer?: boolean): Promise<ArrayBuffer | void> => {
    try {
      // Load ExcelJS library with full styling support
      if (!(window as any).ExcelJS) {
        await loadExcelJSLibrary();
      }

      const ExcelJS = (window as any).ExcelJS;
      
      // Create workbook with ExcelJS (full styling support)
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(resolveExcelMainSheetName(certificateData));
      
      // Set worksheet properties with larger cells for better readability
      worksheet.properties.defaultRowHeight = 30;
      worksheet.properties.defaultColWidth = 20;
      
      // ===== EXTRACT REAL CLIENT DATA FROM METADATA =====
      
      // Extract client info from batch metadata or samples
      let clientInfo = {
        client_name: 'AFRILAB CLIENT',
        project_name: 'GEOLOGICAL ANALYSIS', 
        client_reference: 'SAMPLE ANALYSIS',
        order_number: '',
        receipt_date: new Date().toLocaleDateString('en-GB')
      };
      
      // Try to extract from batch metadata
      if (certificateData.batch_info) {
        const metadata = certificateData.batch_info;
        clientInfo = {
          client_name: metadata.client_name || metadata.clientName || clientInfo.client_name,
          project_name: metadata.project_name || metadata.projectName || metadata.project || clientInfo.project_name,
          client_reference: metadata.client_reference || metadata.clientReference || metadata.reference || clientInfo.client_reference,
          order_number: metadata.order_number || metadata.orderNumber || metadata.order || clientInfo.order_number,
          receipt_date: metadata.receipt_date || metadata.receiptDate || metadata.date_reception || clientInfo.receipt_date
        };
      }
      
      // Try to extract from samples metadata  
      if (certificateData.samples && certificateData.samples.length > 0) {
        const firstSample = certificateData.samples[0];
        if (firstSample.metadata || firstSample.batch_info) {
          const sampleMeta = firstSample.metadata || firstSample.batch_info;
          clientInfo = {
            client_name: sampleMeta.client_name || sampleMeta.clientName || clientInfo.client_name,
            project_name: sampleMeta.project_name || sampleMeta.projectName || sampleMeta.project || clientInfo.project_name,
            client_reference: sampleMeta.client_reference || sampleMeta.clientReference || sampleMeta.reference || clientInfo.client_reference,
            order_number: sampleMeta.order_number || sampleMeta.orderNumber || sampleMeta.order || clientInfo.order_number,
            receipt_date: sampleMeta.receipt_date || sampleMeta.receiptDate || sampleMeta.date_reception || clientInfo.receipt_date
          };
        }
      }
      
      // ===== PREMIUM CORPORATE STYLING (Based on 2024 Design Trends) =====
      
      const headerStyle = {
        font: { bold: true, size: 14, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' }, // Increased font size
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A90D9' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
        border: {
          top: { style: 'medium', color: { argb: 'FF2E5984' } },
          bottom: { style: 'medium', color: { argb: 'FF2E5984' } },
          left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
        }
      };
      
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E79' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const companyStyle = {
        font: { bold: true, size: 22, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
        alignment: { horizontal: 'left', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const dataStyle = {
        font: { size: 12, name: 'Segoe UI' }, // Increased font size
        alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
        border: {
          top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
        }
      };
      
      const dataAlternateStyle = {
        font: { size: 12, name: 'Segoe UI' }, // Increased font size
        alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } },
        border: {
          top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
        }
      };
      
      const subtitleStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const clientHeaderStyle = {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6BAA75' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
        border: {
          top: { style: 'medium', color: { argb: 'FF4A7C59' } },
          bottom: { style: 'medium', color: { argb: 'FF4A7C59' } },
          left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
        }
      };
      
      // ===== BUILD BEAUTIFUL LAYOUT =====
      
      // Row 1: Company Header positioned to the right to avoid logo overlap
      worksheet.mergeCells('E1:L1');
      const afrilab = worksheet.getCell('E1');
      afrilab.value = '✨ AFRILAB - African Laboratory for Mining and Environment ✨';
      afrilab.style = companyStyle;
      
      // Add AFRILAB logo to LEFT SIDE of header (separate from text)
      try {
        // Multiple logo URL attempts for better reliability
        const logoUrls = [
          `${window.location.origin}/logo2.png`,
          `${window.location.origin}/logo.png`,
          '/logo2.png',
          '/logo.png'
        ];
        
        let logoLoaded = false;
        for (const logoUrl of logoUrls) {
          try {
            console.log('Attempting to fetch logo from:', logoUrl);
            
            const logoResponse = await fetch(logoUrl, {
              method: 'GET',
              cache: 'no-cache',
              mode: 'cors'
            });
            
            if (logoResponse.ok) {
              const logoBuffer = await logoResponse.arrayBuffer();
              console.log('Logo loaded successfully from:', logoUrl, 'size:', logoBuffer.byteLength);
              
              const logoImageId = workbook.addImage({
                buffer: logoBuffer,
                extension: 'png',
              });
              
              // Position logo in columns A-D (left side), avoiding text overlap
              worksheet.addImage(logoImageId, {
                tl: { col: 0, row: 0 }, // Start at A1
                br: { col: 3, row: 1 }, // End at D2 - gives logo dedicated space
                editAs: 'absolute'
              });
              console.log('Logo added to Excel successfully in dedicated left area');
              logoLoaded = true;
              break;
            }
          } catch (urlError) {
            console.warn('Failed to load logo from:', logoUrl, urlError);
            continue;
          }
        }
        
        if (!logoLoaded) {
          console.warn('All logo URLs failed to load, using text fallback');
          // Add fallback text in logo area
          worksheet.mergeCells('A1:D1');
          const logoFallback = worksheet.getCell('A1');
          logoFallback.value = 'AFRILAB LOGO';
          logoFallback.style = {
            font: { bold: true, size: 14, color: { argb: 'FF4A90D9' }, name: 'Segoe UI' },
            alignment: { horizontal: 'center', vertical: 'middle' },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
          };
        }
      } catch (error) {
        console.error('Could not load logo for Excel export:', error);
        // Add fallback text in logo area
        worksheet.mergeCells('A1:D1');
        const logoFallback = worksheet.getCell('A1');
        logoFallback.value = 'AFRILAB LOGO';
        logoFallback.style = {
          font: { bold: true, size: 14, color: { argb: 'FF4A90D9' }, name: 'Segoe UI' },
          alignment: { horizontal: 'center', vertical: 'middle' },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
        };
      }
      
      // Add spacing row with increased height for logo
      worksheet.getRow(1).height = 120; // Increased for logo
      worksheet.getRow(2).height = 12;
      
      // Row 3: Report Form Title (moved down for better spacing)
      worksheet.mergeCells('D3:I3');
      const formTitle = worksheet.getCell('D3');
      formTitle.value = '🔬 FORMULAIRE REALISATION 🔬';
      formTitle.style = {
        font: { bold: true, size: 15, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F4F8' } },
        border: {
          top: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          bottom: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          left: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          right: { style: 'thin', color: { argb: 'FFCBD5E0' } }
        }
      };
      
      // Row 5-6: Report Titles (adjusted row numbers)
      worksheet.mergeCells('D5:I5');
      const reportTitle1 = worksheet.getCell('D5');
      reportTitle1.value = '📊 RAPPORT D\'ANALYSE 📊';
      reportTitle1.style = subtitleStyle;
      
      worksheet.mergeCells('D6:I6');
      const reportTitle2 = worksheet.getCell('D6');
      reportTitle2.value = '📈 ANALYTICAL REPORT 📈';
      reportTitle2.style = subtitleStyle;
      
      // Row 6: Certificate Number with Premium Design Box
      worksheet.mergeCells('J6:L6');
      const certCell = worksheet.getCell('J6');
      certCell.value = `🏆 Certificat N° ${certificateNumber} 🏆`;
      certCell.style = {
        font: { bold: true, size: 12, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFBBF24' } },
        border: {
          top: { style: 'thick', color: { argb: 'FFD97706' } },
          bottom: { style: 'thick', color: { argb: 'FFD97706' } },
          left: { style: 'thick', color: { argb: 'FFD97706' } },
          right: { style: 'thick', color: { argb: 'FFD97706' } }
        }
      };
      
      // Row 9-10: Report Info
      const dateCell = worksheet.getCell('A9');
      dateCell.value = 'Report issue date';
      dateCell.style = { font: { bold: true } };
      
      const dateCellValue = worksheet.getCell('C9');
      dateCellValue.value = new Date().toLocaleDateString('en-GB');
      
      const descCell = worksheet.getCell('A10');
      descCell.value = 'Please find below the results of analyzes.';
      
      // ===== CLIENT INFO TABLE WITH BEAUTIFUL HEADERS =====
      
      // Row 12: Headers with blue background
      const clientHeaders = [
        'Report number', 'Company', 'Project', 'Client reference', 'Order number',
        'Imputation code', 'date of receipt', 'Date of analysis', 'Sampled by',
        'Warehouse', 'Prepared by', 'CERTIFICAT'
      ];
      
      clientHeaders.forEach((header, i) => {
        const cell = worksheet.getCell(12, i + 1);
        cell.value = header;
        cell.style = clientHeaderStyle;
      });
      
      // Row 13: Real Client Data
      const reportNumber = `AM/QA/DM/RA/${certificateNumber}/2025 V01`;
      const clientData = [
        reportNumber,
        clientInfo.client_name,
        clientInfo.project_name,
        clientInfo.client_reference,
        clientInfo.order_number,
        '',
        clientInfo.receipt_date,
        new Date().toLocaleDateString('en-GB'),
        'Customer',
        '',
        'AFRILAB',
        certificateNumber
      ];
      
      clientData.forEach((data, i) => {
        const cell = worksheet.getCell(13, i + 1);
        cell.value = data;
        cell.style = dataStyle;
      });
      
      // ===== ANALYSIS TABLE WITH PROFESSIONAL STYLING =====
      
      if (certificateData.certificate_type === 'BATCH' && certificateData.samples) {
        // Detect if this is a BGM batch
        const firstSample = certificateData.samples[0];
        const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
        const isBGMBatch = metadonnees?.bgm_method_selected === true;
        
        if (isBGMBatch) {
          // ===== BGM-SPECIFIC 25-COLUMN STRUCTURE =====
          
          // Define the fixed BGM column structure (25 columns: A-Y)
          const bgmColumns = [
            // Basic info (A-C)
            { key: 'Customers-Ref', header: 'Customers-Ref', method: '', limit: '', unit: '' },
            { key: 'Lab-Ref', header: 'Lab-Ref', method: '', limit: '', unit: '' },
            { key: 'LAB_WEIGHT', header: 'LAB_WEIGHT', method: '', limit: '', unit: 'Kg' },
            
            // SAA Elements (D-H) - HAS VALUES
            { key: 'Ag', header: 'Ag ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
            { key: 'Cu', header: 'Cu', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            { key: 'Fe', header: 'Fe ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Pb', header: 'Pb ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Zn', header: 'Zn', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            
            // SAA Check Elements (I-M) - EMPTY
            { key: 'Ag_CK', header: 'Ag_CK ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
            { key: 'Cu_CK', header: 'Cu_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            { key: 'Fe_CK', header: 'Fe_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Pb_CK', header: 'Pb_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Zn_CK', header: 'Zn_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            
            // Fire Assay Silver (N-O) - EMPTY
            { key: 'Ag_FA', header: 'Ag_FA', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
            { key: 'Ag_FA_CK', header: 'Ag_FA_CK', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
            
            // Fire Assay Gold (P) - HAS VALUES
            { key: 'Au', header: 'Au_FA', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
            
            // ICP Elements (Q-S) - HAS VALUES
            { key: 'Mo', header: 'Mo_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'As', header: 'As_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'Sn', header: 'Sn_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            
            // Gravimetric Gold (T-V) - EMPTY
            { key: 'Au_GRAV', header: 'Au_GRAV', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
            { key: 'Au_GRAV_CK', header: 'Au_GRAV_CK', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
            { key: 'Au_FA_CK', header: 'Au_FA_CK', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
            
            // ICP Check Elements (W-Y) - EMPTY
            { key: 'Mo_ICP_CK', header: 'Mo_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'As_ICP_CK', header: 'As_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'Sn_ICP_CK', header: 'Sn_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' }
          ];
          
          // Row 15: METHOD
          worksheet.mergeCells('A15:C15');
          const methodCell = worksheet.getCell('A15');
          methodCell.value = 'METHOD';
          methodCell.style = headerStyle;
          
          bgmColumns.forEach((col, i) => {
            if (i >= 3) { // Skip A-C (basic info columns)
              const cell = worksheet.getCell(15, i + 1);
              cell.value = col.method;
              cell.style = headerStyle;
            }
          });
          
          // Row 16: LIMITE DETECTION
          worksheet.mergeCells('A16:C16');
          const limitsCell = worksheet.getCell('A16');
          limitsCell.value = 'LIMITE DETECTION';
          limitsCell.style = headerStyle;
          
          bgmColumns.forEach((col, i) => {
            if (i >= 3) { // Skip A-C
              const cell = worksheet.getCell(16, i + 1);
              cell.value = col.limit;
              cell.style = headerStyle;
            }
          });
          
          // Row 17: Headers
          worksheet.getRow(17).height = 40;
          bgmColumns.forEach((col, i) => {
            const cell = worksheet.getCell(17, i + 1);
            cell.value = col.header;
            cell.style = headerStyle;
          });
          
          // Row 18: Units
          worksheet.getRow(18).height = 35;
          bgmColumns.forEach((col, i) => {
            const cell = worksheet.getCell(18, i + 1);
            cell.value = col.unit;
            cell.style = headerStyle;
          });
          
          // Sample Data Rows
          certificateData.samples.forEach((sample: any, index: number) => {
            const rowNum = 19 + index;
            const isEvenRow = index % 2 === 0;
            const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
            
            const receptionWeight = sample.sample_data?.metadonnees?.poids_kg || 
                                   sample.metadonnees?.poids_kg || 
                                   sample.weight || 0.5;
            
            // Build row data according to BGM structure
            const sampleData: any[] = [];
            
            bgmColumns.forEach((col) => {
              if (col.key === 'Customers-Ref') {
                sampleData.push(sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`);
              } else if (col.key === 'Lab-Ref') {
                sampleData.push(sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`);
              } else if (col.key === 'LAB_WEIGHT') {
                sampleData.push(parseFloat(receptionWeight).toFixed(4));
              } else if (col.key === 'Ag') {
                // 🎯 BGM FEATURE: Ag superior limit - show ">200" when value exceeds 200 g/t
                const result = sample.results?.find((r: any) => r.element === 'Ag');
                if (result) {
                  const concentration = parseFloat(result.concentration || 0);
                  if (concentration > 200) {
                    sampleData.push('>200'); // Superior limit exceeded
                  } else {
                    const formatted = result.formatted_concentration;
                    if (formatted !== undefined && formatted !== null) {
                      sampleData.push(String(formatted));
                    } else {
                      sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                    }
                  }
                } else {
                  sampleData.push('');
                }
              } else if (col.key === 'Ag_FA') {
                // 🎯 BGM FEATURE: Ag_FA shows actual value ONLY when Ag > 200 (avoid redundancy)
                const agResult = sample.results?.find((r: any) => r.element === 'Ag');
                if (agResult) {
                  const concentration = parseFloat(agResult.concentration || 0);
                  if (concentration > 200) {
                    // Show actual Ag value (since Ag column shows ">200")
                    const formatted = agResult.formatted_concentration;
                    if (formatted !== undefined && formatted !== null) {
                      sampleData.push(String(formatted));
                    } else {
                      sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                    }
                  } else {
                    // Empty cell (value already shown in Ag column)
                    sampleData.push('');
                  }
                } else {
                  sampleData.push('');
                }
              } else if (col.key.endsWith('_CK') || col.key === 'Au_GRAV' || col.key === 'Au_GRAV_CK' || col.key === 'Au_FA_CK') {
                // Empty check columns
                sampleData.push('');
              } else {
                // Data columns: Cu, Fe, Pb, Zn, Au, Mo, As, Sn
                const result = sample.results?.find((r: any) => r.element === col.key);
                if (result) {
                  const formatted = result.formatted_concentration;
                  if (formatted !== undefined && formatted !== null) {
                    sampleData.push(String(formatted));
                  } else {
                    const concentration = parseFloat(result.concentration || 0);
                    sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                  }
                } else {
                  sampleData.push('');
                }
              }
            });
            
            worksheet.getRow(rowNum).height = 32;
            
            sampleData.forEach((value, i) => {
              const cell = worksheet.getCell(rowNum, i + 1);
              cell.value = value;
              cell.style = currentDataStyle;
            });
          });
          
        } else {
          // ===== NON-BGM BATCHES: ORIGINAL DYNAMIC BEHAVIOR =====
          
          // Use the exact elements list from backend (HTML-aligned ordering)
          const elements: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
          
          // Row 15: METHOD with blue header
          worksheet.mergeCells('A15:C15');
          const methodCell = worksheet.getCell('A15');
          methodCell.value = 'METHOD';
          methodCell.style = headerStyle;
          
          // Show method code per element (from first sample's result), matching how columns are formatted
          const firstSample = certificateData.samples[0];
          elements.forEach((elementName: string, i) => {
            const cell = worksheet.getCell(15, i + 4);
            const res = firstSample?.results?.find((r: any) => r.element === elementName);
            cell.value = res?.method_code || '';
            cell.style = headerStyle;
          });
        
        // Row 16: LIMITE DETECTION with blue header
        worksheet.mergeCells('A16:C16');
        const limitsCell = worksheet.getCell('A16');
        limitsCell.value = 'LIMITE DETECTION';
        limitsCell.style = headerStyle;
        
        elements.forEach((elementName: string, i) => {
          const cell = worksheet.getCell(16, i + 4);
          const res = firstSample?.results?.find((r: any) => r.element === elementName);
          const limit = res?.detection_limit;
          cell.value = typeof limit === 'number' ? limit : '';
          cell.style = headerStyle;
        });
        
        // Row 17: Column Headers with blue background (exact HTML match)
        const headers = ['Customers-Ref', 'Lab-Ref', 'LAB_WEIGHT'];
        elements.forEach((element: string) => headers.push(element + '*')); // Add asterisk like HTML
        
        worksheet.getRow(17).height = 40; // Increased header height
        headers.forEach((header, i) => {
          const cell = worksheet.getCell(17, i + 1);
          cell.value = header;
          cell.style = headerStyle;
        });
        
        // Row 18: Units with blue background (exact HTML match)
        const units = ['', '', 'Kg'];
        elements.forEach((elementName: string) => {
          const res = firstSample?.results?.find((r: any) => r.element === elementName);
          units.push(res?.unit || '');
        });
        
        worksheet.getRow(18).height = 35; // Increased units row height
        units.forEach((unit, i) => {
          const cell = worksheet.getCell(18, i + 1);
          cell.value = unit;
          cell.style = headerStyle;
        });
        
        // Sample Data Rows with Premium Alternating Styling
        certificateData.samples.forEach((sample: any, index: number) => {
          const rowNum = 19 + index;
          const isEvenRow = index % 2 === 0;
          const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
          
          // 🔧 CRITICAL FIX: Use actual reception weight from metadonnees (exactly like HTML)
          const receptionWeight = sample.sample_data?.metadonnees?.poids_kg || 
                                 sample.metadonnees?.poids_kg || 
                                 sample.weight || 0.5;
          
          const sampleData = [
            sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`,
            sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`,
            parseFloat(receptionWeight).toFixed(4) // Format weight to 4 decimals
          ];
          
          elements.forEach((element: string) => {
            const result = sample.results?.find((r: any) => r.element === element);
            if (result) {
              // Prefer backend-provided formatted value which already applies method-specific detection limits
              const formatted = result.formatted_concentration;
              if (formatted !== undefined && formatted !== null) {
                sampleData.push(String(formatted));
              } else {
                // Fallback to raw concentration with minimal formatting
                const concentration = parseFloat(result.concentration || 0);
                sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
              }
            } else {
              // No result found - show a generic below-detection indicator
              sampleData.push('<DL');
            }
          });
          
          // Apply premium styling with generous row height
          worksheet.getRow(rowNum).height = 32; // Much taller data rows for better readability
          
          sampleData.forEach((value, i) => {
            const cell = worksheet.getCell(rowNum, i + 1);
            cell.value = value;
            cell.style = currentDataStyle;
          });
        });
        }
      }

      // ===== ADD QA/QC WORKSHEET IF DATA EXISTS (BGM CERTIFICATES ONLY) =====
      
      if (hasQAQCData(certificateData)) {
        generateQAQCWorksheet(workbook, certificateData, ExcelJS);
      }

      // ===== FINALIZE & DOWNLOAD =====
      
      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();

      if (returnBuffer) return buffer;

      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = certificateNumber.replace(/[^a-zA-Z0-9_\-\s]/g, '').trim().replace(/\s+/g, '_');
      link.download = `${safeName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      throw error;
    }
  };

  // Fixed useEffect - now stable
  useEffect(() => {
    const initializeDashboard = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadDashboardData(),
          loadSamples(),
          loadCertificates(),
          loadNotifications(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, []); // Empty dependency array - only run once on mount

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(refreshData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []); // Empty dependency array for interval

  // Separate useEffect for when filters change
  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  // Load certificates when filters change
  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PRET':
        return <CheckCircleIcon color="success" />;
      case 'RECEPTION':
        return <PendingIcon color="warning" />;
      default:
        return <InProgressIcon color="primary" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PRET':
        return 'success';
      case 'RECEPTION':
        return 'warning';
      case 'ANNULE':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: Record<string, string> = {
      'RECEPTION': 'Réception',
      'PREPARATION_MECANIQUE': 'Préparation',
      'LABORATOIRE_SAA': 'Labo SAA',
      'LABORATOIRE_ICP': 'Labo ICP',
      'LABORATOIRE_AU': 'Labo AU',
      'LABORATOIRE_VOLUMETRIE': 'Labo Volumétrie',
      'VALIDATION_SERVICE': 'Validation',
      'GENERATION_CERTIFICAT': 'Génération Certificat',
      'PRET': 'Prêt',
      'ANNULE': 'Annulé',
    };
    return statusLabels[status] || status;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENTE':
        return 'error';
      case 'ELEVEE':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    const priorityLabels: Record<string, string> = {
      'NORMALE': 'Normale',
      'ELEVEE': 'Élevée',
      'URGENTE': 'Urgente',
    };
    return priorityLabels[priority] || priority;
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string | Date) => {
    return new Date(date).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const clientInfo = dashboardData?.client_info;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%', // Remove width constraints for full width
      mx: 'auto', // Center the content
      overflow: 'hidden',
      p: { xs: 0, sm: 1, md: 2 }, // Reduced padding for more space
      // iOS Safari specific fixes
      WebkitOverflowScrolling: 'touch',
      position: 'relative',
      boxSizing: 'border-box'
    }}>
      {/* Client Header - Enhanced Visual Design */}
      <Paper 
        elevation={6} 
        sx={{ 
          p: { xs: 3, md: 4 }, 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)', 
          color: 'white',
          borderRadius: { xs: 3, md: 4 },
          position: 'relative',
          overflow: 'hidden',
          // iOS Safari fixes
          width: '100%',
          boxSizing: 'border-box',
          WebkitTransform: 'translateZ(0)', // Force hardware acceleration
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Box sx={{ width: '100%', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: { xs: 'none', md: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.25)', 
                    mr: 3,
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    flexShrink: 0,
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                  }}
                >
                  <BusinessIcon fontSize={isMobile ? 'large' : 'inherit'} sx={{ fontSize: { xs: 32, md: 40 } }} />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    variant={isMobile ? "h4" : "h3"} 
                    fontWeight="800"
                    sx={{ 
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      background: 'linear-gradient(45deg, #ffffff 30%, #f8f9ff 90%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      mb: 1
                    }}
                  >
                    {clientInfo?.company_name}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ 
                      opacity: 0.9,
                      fontWeight: 500,
                      letterSpacing: '0.5px'
                    }}
                  >
                    Code Client: {clientInfo?.code}
                  </Typography>
                </Box>
              </Box>
              
              {/* Enhanced Contact Info */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                gap: { xs: 2, sm: 3 },
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                  <EmailIcon sx={{ mr: 1.5, opacity: 0.9, fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                  <Typography 
                    variant={isMobile ? "body2" : "body1"}
                    sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500
                    }}
                  >
                    {clientInfo?.email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <PhoneIcon sx={{ mr: 1.5, opacity: 0.9, fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                  <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 500 }}>
                    {clientInfo?.phone}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ 
              textAlign: { xs: 'center', md: 'right' },
              alignSelf: { xs: 'center', md: 'flex-start' }
            }}>
              <Fab 
                color="secondary" 
                onClick={refreshData} 
                disabled={refreshing}
                size={isMobile ? "medium" : "large"}
                sx={{ 
                  mb: 2,
                  background: 'linear-gradient(45deg, #ff6b6b 30%, #ff8e8e 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #ff5252 30%, #ff7979 90%)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(255,107,107,0.4)'
                }}
              >
                {refreshing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
              </Fab>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Dernière mise à jour: {formatDateTime(new Date())}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Enhanced Key Metrics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 3,
        mb: 4,
        width: '100%'
      }}>
        <Card 
          elevation={8} 
          sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
            color: 'white',
            height: { xs: 170, md: 190 },
            width: '100%',
            boxSizing: 'border-box',
            WebkitTransform: 'translateZ(0)',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 20px 40px rgba(79, 172, 254, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.2) 0%, transparent 50%)',
              pointerEvents: 'none'
            }
          }}
        >
          <CardContent sx={{ 
            textAlign: 'center', 
            p: { xs: 2, md: 3 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            <ScienceIcon sx={{ 
              fontSize: { xs: 32, md: 40 }, 
              mb: 1,
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
            }} />
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="800" sx={{ mb: 0.5 }}>
              {analytics.total_samples || 0}
            </Typography>
            <Typography variant={isMobile ? "body2" : "subtitle1"} sx={{ fontWeight: 600, mb: 1 }}>
              {isSmallMobile ? "Total" : "Échantillons Total"}
            </Typography>
            <Typography variant="caption" sx={{ 
              opacity: 0.9, 
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontWeight: 500,
              bgcolor: 'rgba(255,255,255,0.2)',
              px: 1.5,
              py: 0.5,
              borderRadius: 2
            }}>
              +{analytics.samples_this_month || 0} ce mois
            </Typography>
          </CardContent>
        </Card>
        
        <Card 
          elevation={8} 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            height: { xs: 170, md: 190 },
            width: '100%',
            boxSizing: 'border-box',
            WebkitTransform: 'translateZ(0)',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.2) 0%, transparent 50%)',
              pointerEvents: 'none'
            }
          }}
        >
          <CardContent sx={{ 
            textAlign: 'center', 
            p: { xs: 2, md: 3 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            <InProgressIcon sx={{ 
              fontSize: { xs: 32, md: 40 }, 
              mb: 1,
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
            }} />
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="800" sx={{ mb: 0.5 }}>
              {analytics.samples_in_progress || 0}
            </Typography>
            <Typography variant={isMobile ? "body2" : "subtitle1"} sx={{ fontWeight: 600, mb: 1 }}>
              {isSmallMobile ? "En cours" : "En Cours"}
            </Typography>
            <Typography variant="caption" sx={{ 
              opacity: 0.9, 
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontWeight: 500,
              bgcolor: 'rgba(255,255,255,0.2)',
              px: 1.5,
              py: 0.5,
              borderRadius: 2
            }}>
              Actifs au labo
            </Typography>
          </CardContent>
        </Card>
        
        <Card 
          elevation={8} 
          sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
            color: 'white',
            height: { xs: 170, md: 190 },
            width: '100%',
            boxSizing: 'border-box',
            WebkitTransform: 'translateZ(0)',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 20px 40px rgba(240, 147, 251, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.2) 0%, transparent 50%)',
              pointerEvents: 'none'
            }
          }}
        >
          <CardContent sx={{ 
            textAlign: 'center', 
            p: { xs: 2, md: 3 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            <CheckCircleIcon sx={{ 
              fontSize: { xs: 32, md: 40 }, 
              mb: 1,
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
            }} />
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="800" sx={{ mb: 0.5 }}>
              {analytics.samples_completed || analytics.completed_samples || 0}
            </Typography>
            <Typography variant={isMobile ? "body2" : "subtitle1"} sx={{ fontWeight: 600, mb: 1 }}>
              {isSmallMobile ? "Terminés" : "Terminés"}
            </Typography>
            <Typography variant="caption" sx={{ 
              opacity: 0.9, 
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontWeight: 500,
              bgcolor: 'rgba(255,255,255,0.2)',
              px: 1.5,
              py: 0.5,
              borderRadius: 2
            }}>
              Prêts à récupérer
            </Typography>
          </CardContent>
        </Card>
        
        <Card 
          elevation={8} 
          sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
            color: 'white',
            height: { xs: 170, md: 190 },
            width: '100%',
            boxSizing: 'border-box',
            WebkitTransform: 'translateZ(0)',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 20px 40px rgba(250, 112, 154, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.2) 0%, transparent 50%)',
              pointerEvents: 'none'
            }
          }}
        >
          <CardContent sx={{ 
            textAlign: 'center', 
            p: { xs: 2, md: 3 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            <ScheduleIcon sx={{ 
              fontSize: { xs: 32, md: 40 }, 
              mb: 1,
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
            }} />
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="800" sx={{ mb: 0.5 }}>
              {analytics.average_turnaround_days || 0}
            </Typography>
            <Typography variant={isMobile ? "body2" : "subtitle1"} sx={{ fontWeight: 600, mb: 1 }}>
              {isSmallMobile ? "Jours" : "Jours Moy."}
            </Typography>
            <Typography variant="caption" sx={{ 
              opacity: 0.9, 
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontWeight: 500,
              bgcolor: 'rgba(255,255,255,0.2)',
              px: 1.5,
              py: 0.5,
              borderRadius: 2
            }}>
              Temps de cycle
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Enhanced Main Dashboard Tabs */}
      <Paper 
        elevation={12}
        sx={{ 
          width: '100%',
          overflow: 'hidden',
          WebkitOverflowScrolling: 'touch',
          borderRadius: 4,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.08)'
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          centered={!isMobile}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            width: '100%',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            '& .MuiTabs-scroller': {
              overflow: 'auto !important',
              WebkitOverflowScrolling: 'touch'
            },
            '& .MuiTabs-flexContainer': {
              justifyContent: { xs: 'flex-start', md: 'center' }
            },
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              textTransform: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                transform: 'translateY(-2px)'
              },
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                color: 'primary.main',
                fontWeight: 700
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
            }
          }}
        >
          <Tab 
            icon={<DashboardIcon />} 
            label={isMobile ? "Vue" : "Vue d'ensemble"} 
            sx={{ 
              minWidth: { xs: 80, md: 120 },
              fontSize: { xs: '0.7rem', md: '0.875rem' }
            }}
          />
          <Tab 
            icon={<TimelineIcon />} 
            label="Workflow" 
            sx={{ 
              minWidth: { xs: 80, md: 120 },
              fontSize: { xs: '0.7rem', md: '0.875rem' }
            }}
          />
          <Tab 
            icon={<ScienceIcon />} 
            label="Échantillons" 
            sx={{ 
              minWidth: { xs: 80, md: 120 },
              fontSize: { xs: '0.7rem', md: '0.875rem' },
              display: 'none' // HIDDEN TAB - keeps indices intact
            }}
          />
          <Tab 
            icon={<AssessmentIcon />} 
            label={isMobile ? "Stats" : "Analytiques"} 
            sx={{ 
              minWidth: { xs: 80, md: 120 },
              fontSize: { xs: '0.7rem', md: '0.875rem' }
            }}
          />
          <Tab 
            icon={<DownloadIcon />} 
            label="Certificats" 
            sx={{ 
              minWidth: { xs: 80, md: 120 },
              fontSize: { xs: '0.7rem', md: '0.875rem' }
            }}
          />
          <Tab 
            icon={<Badge badgeContent={notifications.length > 0 ? notifications.length : undefined} color="error">
              <NotificationsIcon />
            </Badge>} 
            label={isMobile ? "Notif" : "Notifications"} 
            sx={{ 
              minWidth: { xs: 80, md: 120 },
              fontSize: { xs: '0.7rem', md: '0.875rem' }
            }}
          />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            {/* Key Performance Indicators */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Aperçu de Votre Activité</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center" p={2}>
                        <Typography variant="h4" color="primary.main" fontWeight="bold">
                          {analytics.total_samples || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Échantillons
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center" p={2}>
                        <Typography variant="h4" color="success.main" fontWeight="bold">
                          {analytics.samples_completed || analytics.completed_samples || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Terminés
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center" p={2}>
                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                          {analytics.samples_in_progress || analytics.pending_samples || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          En Cours
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center" p={2}>
                        <Typography variant="h4" color="info.main" fontWeight="bold">
                          {analytics.certificates_generated || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Certificats
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activity */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Activité Récente</Typography>
                  <List>
                    {dashboardData?.recent_samples?.slice(0, 3).map((sample: any) => (
                      <ListItem key={sample.id} divider>
                        <ListItemIcon>
                          {getStatusIcon(sample.statut)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight="medium">
                                Lab: {sample.sample_id}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • Client: {sample.external_id || 'N/A'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {getStatusLabel(sample.statut)} • {formatDate(sample.date_reception)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    )) || []}
                    {(!dashboardData?.recent_samples || dashboardData.recent_samples.length === 0) && (
                      <ListItem>
                        <ListItemText
                          primary="Aucune activité récente"
                          secondary="Vos échantillons apparaîtront ici une fois soumis."
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Actions Rapides</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<TimelineIcon />}
                      onClick={() => setCurrentTab(1)}
                      fullWidth
                    >
                      Voir Workflow
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => setCurrentTab(4)}
                      fullWidth
                    >
                      Télécharger Certificats
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={refreshData}
                      disabled={refreshing}
                      fullWidth
                    >
                      {refreshing ? 'Actualisation...' : 'Actualiser'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Workflow Tab */}
        <TabPanel value={currentTab} index={1}>
          <ClientWorkflowVisualization 
            onStageClick={(stageId, samples) => {
        
            }}
          />
        </TabPanel>

        {/* Samples Tab - Enhanced Recent Samples Overview */}
        <TabPanel value={currentTab} index={2}>
          <Grid container spacing={4}>
            {/* Enhanced Recent Samples */}
            <Grid item xs={12} lg={8}>
              <Card 
                elevation={8}
                sx={{
                  borderRadius: 4,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  boxShadow: '0 12px 24px rgba(102, 126, 234, 0.15)'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      mb: 3
                    }}
                  >
                    📋 Échantillons Récents
                  </Typography>
                  <List sx={{ '& .MuiListItem-root': { borderRadius: 2, mb: 1 } }}>
                    {dashboardData?.recent_samples?.slice(0, 5).map((sample: any) => (
                      <ListItem 
                        key={sample.id} 
                        divider
                        sx={{
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: 'rgba(102, 126, 234, 0.05)',
                            transform: 'translateX(8px)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Box sx={{
                            p: 1,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getStatusIcon(sample.statut)}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                              <Typography variant="subtitle1" component="span" fontWeight={700}>
                                Lab: {sample.sample_id}
                              </Typography>
                              <Typography variant="subtitle2" component="span" color="text.secondary">
                                • Client: {sample.external_id || 'N/A'}
                              </Typography>
                              <Chip
                                size="small"
                                label={getStatusLabel(sample.statut)}
                                color={getStatusColor(sample.statut) as any}
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                              <Chip
                                size="small"
                                label={getPriorityLabel(sample.priorite)}
                                color={getPriorityColor(sample.priorite) as any}
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>
                                🏗️ {sample.nom_projet || 'Projet non spécifié'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                📅 Reçu: {formatDate(sample.date_reception)}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleDetailedSampleTracking(sample.id, sample)}
                            disabled={trackingLoading}
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    )) || []}
                  </List>
                  {(!dashboardData?.recent_samples || dashboardData.recent_samples.length === 0) && (
                    <Box 
                      textAlign="center" 
                      py={6}
                      sx={{
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                        borderRadius: 3,
                        border: '2px dashed rgba(102, 126, 234, 0.2)'
                      }}
                    >
                      <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" fontWeight={600}>
                        Aucune activité récente
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vos échantillons apparaîtront ici une fois soumis.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Enhanced Quick Stats */}
            <Grid item xs={12} lg={4}>
              <Card 
                elevation={8}
                sx={{
                  borderRadius: 4,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f0f8ff 100%)',
                  border: '1px solid rgba(79, 172, 254, 0.2)',
                  boxShadow: '0 12px 24px rgba(79, 172, 254, 0.15)'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    📊 Statistiques Rapides
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2.5}>
                    <Box 
                      display="flex" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                        border: '1px solid rgba(33, 150, 243, 0.2)'
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>📥 En Réception:</Typography>
                      <Chip 
                        label={analytics.status_distribution?.find(s => s.status === 'RECEPTION')?.count || 0} 
                        size="small" 
                        color="info"
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <Box 
                      display="flex" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
                        border: '1px solid rgba(255, 152, 0, 0.2)'
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>⚙️ En Préparation:</Typography>
                      <Chip 
                        label={analytics.status_distribution?.find(s => s.status === 'PREPARATION_MECANIQUE')?.count || 0} 
                        size="small" 
                        color="warning"
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <Box 
                      display="flex" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(102, 126, 234, 0.05) 100%)',
                        border: '1px solid rgba(102, 126, 234, 0.2)'
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>🔬 En Analyse:</Typography>
                      <Chip 
                        label={analytics.status_distribution?.filter(s => ['LABORATOIRE_SAA', 'LABORATOIRE_ICP', 'LABORATOIRE_AU', 'LABORATOIRE_VOLUMETRIE'].includes(s.status)).reduce((sum, curr) => sum + curr.count, 0) || 0} 
                        size="small" 
                        color="primary"
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <Box 
                      display="flex" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                        border: '1px solid rgba(76, 175, 80, 0.2)'
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>✅ Terminés:</Typography>
                      <Chip 
                        label={analytics.status_distribution?.find(s => s.status === 'PRET')?.count || 0} 
                        size="small" 
                        color="success"
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                    <Box 
                      display="flex" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }}
                    >
                      <Typography variant="body1" fontWeight={700}>📊 Total:</Typography>
                      <Chip 
                        label={analytics.total_samples || 0} 
                        size="medium" 
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 800,
                          fontSize: '0.875rem'
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Enhanced Detailed Samples Section */}
          <Card 
            elevation={8} 
            sx={{ 
              mt: 4,
              borderRadius: 4,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
              boxShadow: '0 12px 24px rgba(102, 126, 234, 0.15)'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Typography 
                  variant="h5"
                  sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  🧪 Tous Mes Échantillons
                  {samples.length > 0 && (
                    <Chip 
                      size="small" 
                      label={`${samples.length} échantillons`} 
                      sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <TextField
                    size="small"
                    placeholder="🔍 Rechercher... (optimisé pour grandes données)"
                    onChange={(e) => handleSearchChange(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 200 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        background: 'rgba(102, 126, 234, 0.05)'
                      }
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={sampleFilters.status}
                      onChange={(e) => setSampleFilters(prev => ({ ...prev, status: e.target.value }))}
                      label="Statut"
                      sx={{ 
                        borderRadius: 3,
                        background: 'rgba(102, 126, 234, 0.05)'
                      }}
                    >
                      <MenuItem value="">Tous</MenuItem>
                      <MenuItem value="RECEPTION">Réception</MenuItem>
                      <MenuItem value="PREPARATION_MECANIQUE">Préparation</MenuItem>
                      <MenuItem value="PRET">Prêt</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadSamples()}
                    disabled={refreshing}
                    size="small"
                    sx={{
                      borderRadius: 3,
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                      color: '#667eea',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                        borderColor: '#667eea'
                      }
                    }}
                  >
                    Actualiser
                  </Button>
                </Box>
              </Box>

              {/* Batch samples display logic preserved */}
              {(() => {
                const groupSamplesByBatch = (samples: any[]) => {
                  const batches: Record<string, any[]> = {};
                  const individualSamples: any[] = [];

                  samples.forEach(sample => {
                    const batchInfo = sample.metadonnees?.batch_info || sample.metadonnees?.batch_analysis_summary;
                    if (batchInfo && (batchInfo.is_batch_sample || batchInfo.batch_number)) {
                      const batchNumber = batchInfo.batch_number;
                      if (!batches[batchNumber]) {
                        batches[batchNumber] = [];
                      }
                      batches[batchNumber].push(sample);
                    } else {
                      individualSamples.push(sample);
                    }
                  });

                  return { batches, individualSamples };
                };

                const getBatchPosition = (sample: any): number => {
                  const batchInfo = sample.metadonnees?.batch_info || sample.metadonnees?.batch_analysis_summary;
                  return batchInfo?.sample_position || batchInfo?.position_in_batch || 1;
                };

                const getTotalSamplesInBatch = (sample: any): number => {
                  const batchInfo = sample.metadonnees?.batch_info || sample.metadonnees?.batch_analysis_summary;
                  return batchInfo?.total_samples || batchInfo?.total_samples_in_batch || 1;
                };

                const { batches, individualSamples } = groupSamplesByBatch(samples);
                const hasBatches = Object.keys(batches).length > 0;

                return (
                  <Box>
                    {hasBatches && (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: 3,
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                          border: '1px solid rgba(33, 150, 243, 0.2)'
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          🧪 Échantillons organisés par lots. {Object.keys(batches).length} lot(s) détecté(s).
                        </Typography>
                      </Alert>
                    )}

                    {Object.entries(batches).map(([batchNumber, batchSamples]: [string, any[]]) => {
                      const isExpanded = expandedBatches.has(batchNumber);
                      
                      return (
                        <Box key={batchNumber} sx={{ mb: hasBatches ? 4 : 0 }}>
                          <Box 
                            sx={{ 
                              mb: 3, 
                              p: 3, 
                              background: isExpanded 
                                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)' 
                                : 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)', 
                              borderRadius: 3, 
                              border: '2px solid', 
                              borderColor: isExpanded ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                                borderColor: 'rgba(102, 126, 234, 0.4)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)'
                              }
                            }}
                            onClick={() => toggleBatchExpansion(batchNumber)}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Box>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 1
                                  }}
                                >
                                  <BusinessIcon />
                                  📦 Lot: {batchNumber}
                                  <Chip 
                                    label={batchSamples.length} 
                                    size="small" 
                                    sx={{ 
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      color: 'white',
                                      fontWeight: 700
                                    }}
                                  />
                                </Typography>
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  {batchSamples.length} échantillon(s) dans ce lot • 
                                  <strong style={{ color: isExpanded ? '#667eea' : '#764ba2' }}>
                                    {' '}Cliquez pour {isExpanded ? 'masquer' : 'afficher'} les détails
                                  </strong>
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={2}>
                                {!isExpanded && (
                                  <Box display="flex" gap={1}>
                                    {Array.from(new Set(batchSamples.map(s => s.statut))).slice(0, 3).map((status, idx) => (
                                      <Chip 
                                        key={idx}
                                        size="small" 
                                        label={getStatusLabel(status)}
                                        color={getStatusColor(status) as any}
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                                <Box 
                                  sx={{ 
                                    p: 1,
                                    borderRadius: '50%',
                                    background: isExpanded 
                                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                                      : 'rgba(102, 126, 234, 0.2)',
                                    color: isExpanded ? 'white' : '#667eea'
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Box>
                              </Box>
                            </Box>
                          </Box>

                          {isExpanded && (
                            <Box sx={{ 
                              pl: 2,
                              borderLeft: '3px solid',
                              borderColor: 'rgba(102, 126, 234, 0.3)'
                            }}>
                              {/* Show simplified sample cards when expanded */}
                              <Box>
                                {batchSamples.map((sample: any) => (
                                  <Card 
                                    key={sample.id} 
                                    variant="outlined" 
                                    sx={{ 
                                      mb: 2,
                                      borderRadius: 3,
                                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                                      border: '1px solid rgba(102, 126, 234, 0.15)',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        transform: 'translateX(8px)',
                                        boxShadow: '0 8px 16px rgba(102, 126, 234, 0.2)'
                                      }
                                    }}
                                  >
                                    <CardContent sx={{ p: 3 }}>
                                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                        <Box>
                                          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                            🧪 Lab: {sample.sample_id}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            👤 Client: {sample.external_id || 'N/A'}
                                          </Typography>
                                          <Typography variant="caption" sx={{ 
                                            color: '#667eea',
                                            fontWeight: 600,
                                            background: 'rgba(102, 126, 234, 0.1)',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1
                                          }}>
                                            📦 Sac {getBatchPosition(sample)}/{getTotalSamplesInBatch(sample)}
                                          </Typography>
                                        </Box>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                          <Chip
                                            size="small"
                                            label={getStatusLabel(sample.statut)}
                                            color={getStatusColor(sample.statut) as any}
                                            sx={{ fontWeight: 600 }}
                                          />
                                        </Box>
                                      </Box>
                                      
                                      <Box display="flex" gap={1.5} flexWrap="wrap">
                                        <Button
                                          size="small"
                                          startIcon={<LocationIcon />}
                                          onClick={() => handleSampleLocation(sample.id, sample)}
                                          variant="outlined"
                                          sx={{
                                            borderRadius: 2,
                                            borderColor: 'rgba(33, 150, 243, 0.5)',
                                            color: '#2196f3',
                                            '&:hover': { background: 'rgba(33, 150, 243, 0.1)' }
                                          }}
                                        >
                                          Localiser
                                        </Button>
                                        <Button
                                          size="small"
                                          startIcon={<TimelineIcon />}
                                          onClick={() => handleDetailedSampleTracking(sample.id, sample)}
                                          variant="outlined"
                                          sx={{
                                            borderRadius: 2,
                                            borderColor: 'rgba(102, 126, 234, 0.5)',
                                            color: '#667eea',
                                            '&:hover': { background: 'rgba(102, 126, 234, 0.1)' }
                                          }}
                                        >
                                          Suivi
                                        </Button>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })}

                    {/* Individual samples section */}
                    {individualSamples.length > 0 && (
                      <Box>
                        {hasBatches && (
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              mt: 4, 
                              mb: 3,
                              fontWeight: 700,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text'
                            }}
                          >
                            🔬 Échantillons Individuels
                          </Typography>
                        )}

                        <Box>
                          {individualSamples.map((sample: any) => (
                            <Card 
                              key={sample.id} 
                              variant="outlined" 
                              sx={{ 
                                mb: 2,
                                borderRadius: 3,
                                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                                border: '1px solid rgba(102, 126, 234, 0.15)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateX(8px)',
                                  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.2)'
                                }
                              }}
                            >
                              <CardContent sx={{ p: 3 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                      🧪 Lab: {sample.sample_id}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      👤 Client: {sample.external_id || 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" gap={1} flexWrap="wrap">
                                    <Chip
                                      size="small"
                                      label={getStatusLabel(sample.statut)}
                                      color={getStatusColor(sample.statut) as any}
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Box>
                                </Box>
                                
                                <Box display="flex" gap={1.5} flexWrap="wrap">
                                  <Button
                                    size="small"
                                    startIcon={<LocationIcon />}
                                    onClick={() => handleSampleLocation(sample.id, sample)}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 2,
                                      borderColor: 'rgba(33, 150, 243, 0.5)',
                                      color: '#2196f3',
                                      '&:hover': { background: 'rgba(33, 150, 243, 0.1)' }
                                    }}
                                  >
                                    Localiser
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<TimelineIcon />}
                                    onClick={() => handleDetailedSampleTracking(sample.id, sample)}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 2,
                                      borderColor: 'rgba(102, 126, 234, 0.5)',
                                      color: '#667eea',
                                      '&:hover': { background: 'rgba(102, 126, 234, 0.1)' }
                                    }}
                                  >
                                    Suivi
                                  </Button>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Enhanced Pagination */}
              <Box sx={{ 
                mt: 4, 
                p: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
              }}>
                <TablePagination
                  component="div"
                  count={analytics.total_samples || 0}
                  page={samplePage}
                  onPageChange={(_, newPage) => setSamplePage(newPage)}
                  rowsPerPage={sampleRowsPerPage}
                  onRowsPerPageChange={(e) => setSampleRowsPerPage(parseInt(e.target.value, 10))}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelDisplayedRows={({ from, to }) => `${from}-${to}`}
                  labelRowsPerPage="Lignes par page:"
                  sx={{ 
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontWeight: 600
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={currentTab} index={3}>
          <Box>
            <Typography variant="h5" gutterBottom>
              📊 Tableau de Bord Analytique
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Analyses détaillées des performances, tendances et métriques de qualité
            </Typography>

            {/* Analytics Overview Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ minHeight: 60 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                          {analytics?.total_samples || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                          Échantillons Total
                        </Typography>
                      </Box>
                      <ScienceIcon sx={{ fontSize: 32, opacity: 0.8, mt: 1 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2} sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ minHeight: 60 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                          {analytics?.samples_completed || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                          Terminés
                        </Typography>
                      </Box>
                      <CheckCircleIcon sx={{ fontSize: 32, opacity: 0.8, mt: 1 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2} sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ minHeight: 60 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                          {analytics?.samples_in_progress || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                          En Cours
                        </Typography>
                      </Box>
                      <InProgressIcon sx={{ fontSize: 32, opacity: 0.8, mt: 1 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2} sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ minHeight: 60 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                          {analytics?.average_turnaround_days || 0}j
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                          Délai Moyen
                        </Typography>
                      </Box>
                      <ScheduleIcon sx={{ fontSize: 32, opacity: 0.8, mt: 1 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Status Distribution Chart */}
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                      <AssessmentIcon sx={{ mr: 1 }} />
                      Répartition par Statut
                    </Typography>
                    {analytics?.status_distribution && analytics.status_distribution.length > 0 ? (
                      <Box sx={{ height: 350 }}>
                        <Doughnut
                          data={{
                            labels: analytics.status_distribution.map((item: any) => getStatusLabel(item.status)),
                            datasets: [
                              {
                                data: analytics.status_distribution.map((item: any) => item.count),
                                backgroundColor: analytics.status_distribution.map((item: any) => item.color || '#757575'),
                                borderWidth: 3,
                                borderColor: '#fff',
                                hoverBorderWidth: 4,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: {
                                  usePointStyle: true,
                                  padding: 20,
                                  font: { size: 12 }
                                },
                              },
                              tooltip: {
                                callbacks: {
                                  label: (context: any) => {
                                    const item = analytics.status_distribution[context.dataIndex];
                                    return `${context.label}: ${item.count} (${item.percentage}%)`;
                                  }
                                }
                              }
                            },
                            cutout: 60,
                          }}
                        />
                      </Box>
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography color="text.secondary">Aucune donnée disponible</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Trends */}
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                      <TimelineIcon sx={{ mr: 1 }} />
                      Tendances de Performance
                    </Typography>
                    {analytics?.monthly_trends && analytics.monthly_trends.length > 0 ? (
                      <Box sx={{ height: 350 }}>
                        <Bar
                          data={{
                            labels: analytics.monthly_trends.map((item: any) => item.month),
                            datasets: [
                              {
                                label: 'Échantillons Soumis',
                                data: analytics.monthly_trends.map((item: any) => item.samples_submitted || 0),
                                backgroundColor: 'rgba(33, 150, 243, 0.8)',
                                borderColor: '#2196f3',
                                borderWidth: 1,
                                borderRadius: 4,
                                borderSkipped: false,
                              },
                              {
                                label: 'Échantillons Terminés',
                                data: analytics.monthly_trends.map((item: any) => item.samples_completed || 0),
                                backgroundColor: 'rgba(76, 175, 80, 0.8)',
                                borderColor: '#4caf50',
                                borderWidth: 1,
                                borderRadius: 4,
                                borderSkipped: false,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top' as const,
                                labels: {
                                  usePointStyle: true,
                                  padding: 20,
                                }
                              },
                              title: {
                                display: true,
                                text: 'Évolution Mensuelle des Échantillons',
                                font: {
                                  size: 16,
                                  weight: 'bold'
                                }
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                titleColor: 'white',
                                bodyColor: 'white',
                                cornerRadius: 8,
                                displayColors: true
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                grid: {
                                  color: 'rgba(0,0,0,0.1)',
                                },
                                ticks: {
                                  stepSize: 1
                                }
                              },
                              x: {
                                grid: {
                                  display: false,
                                },
                                ticks: {
                                  maxRotation: 45
                                }
                              },
                            },
                            interaction: {
                              intersect: false,
                              mode: 'index' as const,
                            },
                            barThickness: 'flex',
                            categoryPercentage: 0.8,
                            barPercentage: 0.9,
                          }}
                        />
                      </Box>
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography color="text.secondary">Aucune tendance disponible</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Sample Type & Laboratory Distribution */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                      <BusinessIcon sx={{ mr: 1 }} />
                      Répartition par Type d'Échantillon
                    </Typography>
                    {analytics?.sample_type_distribution && analytics.sample_type_distribution.length > 0 ? (
                      <Box sx={{ height: 300 }}>
                        <Bar
                          data={{
                            labels: analytics.sample_type_distribution.map((item: any) => item.type),
                            datasets: [
                              {
                                label: 'Nombre d\'échantillons',
                                data: analytics.sample_type_distribution.map((item: any) => item.count),
                                backgroundColor: [
                                  '#FF6384',
                                  '#36A2EB',
                                  '#FFCE56',
                                  '#4BC0C0',
                                  '#9966FF',
                                  '#FF9F40'
                                ],
                                borderWidth: 1,
                                borderRadius: 4,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false,
                              },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  stepSize: 1,
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height={250}>
                        <Typography color="text.secondary">Aucune donnée de type disponible</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                      <ScienceIcon sx={{ mr: 1 }} />
                      Distribution Laboratoire
                    </Typography>
                    {analytics?.laboratory_distribution && analytics.laboratory_distribution.length > 0 ? (
                      <List>
                        {analytics.laboratory_distribution.map((lab: any, index: number) => (
                          <ListItem key={index} divider>
                            <ListItemIcon>
                              <Avatar sx={{ bgcolor: `hsl(${index * 60}, 70%, 50%)`, width: 32, height: 32 }}>
                                {lab.laboratory?.charAt(0) || 'L'}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={lab.laboratory || 'Laboratoire Inconnu'}
                              secondary={`${lab.count} échantillons • Délai moyen: ${lab.avg_turnaround?.toFixed(1) || 'N/A'} jours`}
                            />
                            <ListItemSecondaryAction>
                              <Chip
                                size="small"
                                label={lab.count}
                                color="primary"
                                variant="outlined"
                              />
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height={250}>
                        <Typography color="text.secondary">Aucune donnée de laboratoire disponible</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Performance Metrics */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                      <TrendingUpIcon sx={{ mr: 1 }} />
                      Métriques de Performance Détaillées
                    </Typography>
                    
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            {analytics?.fastest_turnaround_days || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Délai le Plus Rapide (jours)
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                          <Typography variant="h4" color="warning.main" fontWeight="bold">
                            {analytics?.slowest_turnaround_days || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Délai le Plus Lent (jours)
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                          <Typography variant="h4" color="success.main" fontWeight="bold">
                            {analytics?.on_time_delivery_percentage || 0}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Livraison à Temps
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                          <Typography variant="h4" color="info.main" fontWeight="bold">
                            {analytics?.samples_this_month || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ce Mois-ci
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Priority Analysis */}
                    {analytics?.priority_analysis && analytics.priority_analysis.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                          Analyse par Priorité
                        </Typography>
                        <Grid container spacing={2}>
                          {analytics.priority_analysis.map((priority: any, index: number) => (
                            <Grid item xs={12} sm={4} key={index}>
                              <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {priority.priority || 'NORMALE'}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  {priority.count} échantillons
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Délai moyen: {priority.avg_turnaround?.toFixed(1) || 'N/A'} jours
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Certificates Tab */}
        <TabPanel value={currentTab} index={4}>
          <Card elevation={2}>
            <CardContent>
              {/* Header */}
              <Box display="flex" justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={2} flexDirection={isMobile ? "column" : "row"} gap={isMobile ? 2 : 0}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">Mes Certificats</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {certificateTotal} certificat{certificateTotal !== 1 ? 's' : ''}
                    {(certificateFilters.dateFrom || certificateFilters.dateTo) && ' dans la période sélectionnée'}
                  </Typography>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {(certificateFilters.dateFrom && certificateFilters.dateTo) && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={bulkExporting ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                      onClick={handleBulkExcelExport}
                      disabled={bulkExporting}
                      size="small"
                      sx={{ borderRadius: 2 }}
                    >
                      {bulkExporting
                        ? `Export ${bulkExportProgress.current}/${bulkExportProgress.total}...`
                        : `Exporter tout (Excel ZIP)`}
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadCertificates()} disabled={refreshing || certificatesLoading} size="small" sx={{ borderRadius: 2 }}>
                    Actualiser
                  </Button>
                </Box>
              </Box>

              {/* Search and Filters */}
              <Box display="flex" gap={1.5} flexWrap="wrap" mb={3} alignItems="center" sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  size="small"
                  placeholder="Rechercher (min 3 car.) lot ou ref..."
                  value={certificateSearchInput}
                  onChange={(e) => {
                    setCertificateSearchInput(e.target.value);
                    handleCertificateSearchChange(e.target.value);
                  }}
                  helperText={certificateSearchInput.length > 0 && certificateSearchInput.length < 3 ? 'Tapez au moins 3 caractères' : ''}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  }}
                  sx={{ minWidth: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                {/* Date Range Picker Trigger */}
                <Button
                  variant={certificateFilters.dateFrom ? 'contained' : 'outlined'}
                  color={certificateFilters.dateFrom ? 'primary' : 'inherit'}
                  startIcon={<CalendarIcon />}
                  onClick={(e) => {
                    setTempDateFrom(certificateFilters.dateFrom ? new Date(certificateFilters.dateFrom) : null);
                    setTempDateTo(certificateFilters.dateTo ? new Date(certificateFilters.dateTo) : null);
                    setDatePickerAnchor(e.currentTarget);
                  }}
                  size="small"
                  sx={{ borderRadius: 2, textTransform: 'none', minWidth: 160 }}
                >
                  {datePickerLabel}
                </Button>

                {(certificateFilters.search || certificateFilters.dateFrom) && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={() => {
                      setCertificateFilters({ search: '', dateFrom: '', dateTo: '' });
                      setCertificateSearchInput('');
                      setDatePickerLabel('Période');
                      setCertificatePage(0);
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Effacer filtres
                  </Button>
                )}
              </Box>

              {/* Date Range Popover */}
              <Popover
                open={Boolean(datePickerAnchor)}
                anchorEl={datePickerAnchor}
                onClose={() => setDatePickerAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { borderRadius: 3, p: 2, maxWidth: 620 } }}
              >
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                  {/* Shortcut Chips */}
                  <Box display="flex" gap={0.5} flexWrap="wrap" mb={2}>
                    {dateShortcuts.map((shortcut) => (
                      <Chip
                        key={shortcut.label}
                        label={shortcut.label}
                        size="small"
                        clickable
                        color={datePickerLabel === shortcut.label ? 'primary' : 'default'}
                        variant={datePickerLabel === shortcut.label ? 'filled' : 'outlined'}
                        onClick={() => {
                          const [from, to] = shortcut.getRange();
                          applyDateRange(from as Date, to as Date, shortcut.label);
                        }}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {/* Calendar Pickers */}
                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ ml: 1 }}>Date début</Typography>
                      <DateCalendar
                        value={tempDateFrom}
                        onChange={(date) => setTempDateFrom(date)}
                        sx={{ width: 280 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ ml: 1 }}>Date fin</Typography>
                      <DateCalendar
                        value={tempDateTo}
                        onChange={(date) => setTempDateTo(date)}
                        sx={{ width: 280 }}
                      />
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button size="small" onClick={() => { applyDateRange(null, null, 'Période'); }} color="inherit">Effacer</Button>
                    <Button size="small" variant="contained" onClick={() => applyDateRange(tempDateFrom, tempDateTo)} disabled={!tempDateFrom || !tempDateTo}>Appliquer</Button>
                  </Box>
                </LocalizationProvider>
              </Popover>

              {/* Bulk Export Progress */}
              {bulkExporting && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate" value={bulkExportProgress.total ? (bulkExportProgress.current / bulkExportProgress.total) * 100 : 0} sx={{ borderRadius: 2, height: 6 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Génération des fichiers Excel: {bulkExportProgress.current} / {bulkExportProgress.total}
                  </Typography>
                </Box>
              )}

              {/* Loading Skeleton */}
              {certificatesLoading ? (
                <Box>
                  {[...Array(5)].map((_, i) => (
                    <Box key={i} display="flex" gap={2} mb={1.5} alignItems="center">
                      <Skeleton variant="text" width="30%" height={32} />
                      <Skeleton variant="text" width="15%" height={32} />
                      <Skeleton variant="text" width="20%" height={32} />
                      <Skeleton variant="text" width="15%" height={32} />
                      <Skeleton variant="rectangular" width="10%" height={28} sx={{ borderRadius: 1 }} />
                    </Box>
                  ))}
                </Box>
              ) : certificates.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {certificateFilters.search || certificateFilters.dateFrom
                      ? 'Aucun certificat trouvé avec ces critères'
                      : 'Aucun certificat disponible'}
                  </Typography>
                </Box>
              ) : isMobile ? (
                <Box>
                  {certificates.map((cert: any) => (
                    <Card key={cert.id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {cert.display_name || cert.certificate_number}
                        </Typography>
                        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                          <Chip size="small" label={cert.lot_ref || cert.sample_id} variant="outlined" />
                          <Chip size="small" label={cert.client_reference || 'N/A'} variant="outlined" color="primary" />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                          {formatDate(cert.generated_at)}
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Button size="small" startIcon={<ViewIcon />} onClick={() => handleCertificateView(cert.id)} variant="outlined" sx={{ borderRadius: 2 }}>Voir</Button>
                          <Button size="small" startIcon={<PictureAsPdf />} onClick={() => handleCertificatePDF(cert.id)} variant="outlined" color="warning" sx={{ borderRadius: 2 }}>PDF</Button>
                          <Button size="small" startIcon={<TableChart />} onClick={() => handleCertificateExcel(cert.id, cert.display_name)} variant="outlined" color="success" sx={{ borderRadius: 2 }}>Excel</Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Certificat</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>N° Lot</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ref. Client</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {certificates.map((cert: any, idx: number) => (
                        <TableRow key={cert.id} hover sx={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'action.hover' }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {cert.display_name || cert.certificate_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={cert.lot_ref || cert.sample_id} variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {cert.client_reference || cert.sample_external_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(cert.generated_at)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={0.5} justifyContent="center">
                              <Tooltip title="Voir (HTML)">
                                <IconButton size="small" onClick={() => handleCertificateView(cert.id)}><ViewIcon fontSize="small" /></IconButton>
                              </Tooltip>
                              <Tooltip title="Export PDF">
                                <IconButton size="small" color="warning" onClick={() => handleCertificatePDF(cert.id)}><PictureAsPdf fontSize="small" /></IconButton>
                              </Tooltip>
                              <Tooltip title="Export Excel">
                                <IconButton size="small" color="success" onClick={() => handleCertificateExcel(cert.id, cert.display_name)}><TableChart fontSize="small" /></IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination
                component="div"
                count={certificateTotal}
                page={certificatePage}
                onPageChange={(_, newPage) => setCertificatePage(newPage)}
                rowsPerPage={certificateRowsPerPage}
                onRowsPerPageChange={(e) => { setCertificateRowsPerPage(parseInt(e.target.value, 10)); setCertificatePage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Lignes par page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Enhanced Notifications Tab */}
        <TabPanel value={currentTab} index={5}>
          <Box>
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              🔔 Centre de Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Restez informé de l'évolution de vos échantillons et des événements importants
            </Typography>

            {/* Notification Stats Summary */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={4}
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <NotificationsIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold">
                      {notifications.length}
                    </Typography>
                    <Typography variant="body2">
                      Total Notifications
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={4}
                  sx={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Badge badgeContent={notifications.filter(n => !n.read).length} color="error">
                      <WarningIcon sx={{ fontSize: 32, mb: 1 }} />
                    </Badge>
                    <Typography variant="h4" fontWeight="bold">
                      {notifications.filter(n => !n.read).length}
                    </Typography>
                    <Typography variant="body2">
                      Non Lues
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={4}
                  sx={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold">
                      {notifications.filter(n => n.type === 'sample_ready').length}
                    </Typography>
                    <Typography variant="body2">
                      Échantillons Prêts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={4}
                  sx={{ 
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <DownloadIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold">
                      {notifications.filter(n => n.type === 'certificate_ready').length}
                    </Typography>
                    <Typography variant="body2">
                      Certificats Prêts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Notification Filters */}
            <Card elevation={6} sx={{ mb: 4, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    🔍 Filtres:
                  </Typography>
                  <Button
                    variant={notifications.filter(n => !n.read).length === notifications.length ? "contained" : "outlined"}
                    size="small"
                    startIcon={<Badge badgeContent={notifications.filter(n => !n.read).length} color="error"><NotificationsIcon /></Badge>}
                    sx={{ borderRadius: 2 }}
                  >
                    Non Lues ({notifications.filter(n => !n.read).length})
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Échantillons Prêts
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Certificats
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ScienceIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Analyses
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadNotifications()}
                    disabled={refreshing}
                    sx={{ borderRadius: 2, ml: 'auto' }}
                  >
                    Actualiser
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Enhanced Notifications List */}
            {notifications.length > 0 ? (
              <Card elevation={8} sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 0 }}>
                  <List sx={{ width: '100%' }}>
                    {notifications.map((notification: any, index: number) => {
                      const isUrgent = notification.priority === 'high' || notification.priority === 'HIGH';
                      const isUnread = !notification.read;
                      
                      return (
                        <React.Fragment key={notification.id || index}>
                          <ListItem
                            sx={{
                              p: 3,
                              background: isUnread 
                                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                                : 'transparent',
                              borderLeft: isUrgent ? '4px solid #f44336' : isUnread ? '4px solid #667eea' : '4px solid transparent',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                                transform: 'translateX(4px)',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                              }
                            }}
                          >
                            <ListItemIcon>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: '50%',
                                background: isUrgent 
                                  ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
                                  : notification.type === 'sample_ready' 
                                    ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                    : notification.type === 'certificate_ready'
                                      ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                                      : notification.type === 'analysis_started'
                                        ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {notification.type === 'sample_ready' && <CheckCircleIcon />}
                                {notification.type === 'certificate_ready' && <DownloadIcon />}
                                {notification.type === 'analysis_started' && <ScienceIcon />}
                                {notification.type === 'quality_check' && <WarningIcon />}
                                {!['sample_ready', 'certificate_ready', 'analysis_started', 'quality_check'].includes(notification.type) && <InfoIcon />}
                              </Box>
                            </ListItemIcon>
                            
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                                  <Typography 
                                    variant="h6" 
                                    fontWeight={isUnread ? 700 : 600}
                                    sx={{ 
                                      color: isUnread ? 'text.primary' : 'text.secondary',
                                      flex: 1
                                    }}
                                  >
                                    {notification.title}
                                  </Typography>
                                  {isUnread && (
                                    <Chip 
                                      label="NOUVEAU" 
                                      size="small" 
                                      sx={{ 
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: '0.7rem'
                                      }}
                                    />
                                  )}
                                  {isUrgent && (
                                    <Chip 
                                      label="URGENT" 
                                      size="small" 
                                      color="error"
                                      sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      mb: 1,
                                      fontWeight: isUnread ? 500 : 400,
                                      color: 'text.primary'
                                    }}
                                  >
                                    {notification.message}
                                  </Typography>
                                  {notification.sample_id && (
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                      <ScienceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="primary" fontWeight={600}>
                                        Échantillon: {notification.sample_id}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      📅 {formatDateTime(notification.date || notification.created_at)}
                                    </Typography>
                                    <Box display="flex" gap={1}>
                                      {notification.sample_id && (
                                        <Button
                                          size="small"
                                          startIcon={<ViewIcon />}
                                          onClick={() => handleDetailedSampleTracking(notification.sample_id, { sample_id: notification.sample_id })}
                                          sx={{ 
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600
                                          }}
                                        >
                                          Voir Échantillon
                                        </Button>
                                      )}
                                      {!notification.read && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<CheckCircleIcon />}
                                          sx={{ 
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600
                                          }}
                                        >
                                          Marquer lu
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < notifications.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </CardContent>
              </Card>
            ) : (
              <Card 
                elevation={8}
                sx={{ 
                  borderRadius: 4,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                  border: '2px dashed rgba(102, 126, 234, 0.2)'
                }}
              >
                <CardContent sx={{ p: 6, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <NotificationsIcon 
                      sx={{ 
                        fontSize: 80, 
                        color: 'text.secondary',
                        opacity: 0.5
                      }} 
                    />
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight={600} color="text.secondary">
                    Aucune notification
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Vos notifications importantes apparaîtront ici une fois que vos échantillons progresseront dans le système.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadNotifications()}
                    disabled={refreshing}
                    sx={{
                      borderRadius: 3,
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                      color: '#667eea',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                        borderColor: '#667eea'
                      }
                    }}
                  >
                    Actualiser les Notifications
                  </Button>
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Sample Tracking Dialog */}
      <Dialog 
        open={trackingDialogOpen} 
        onClose={() => setTrackingDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Suivi de l'Échantillon
          {!isMobile && (
            <IconButton
              onClick={() => setTrackingDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              ×
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {sampleTracking && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Lab: {sampleTracking.sample.sample_id} • Client: {sampleTracking.sample.external_id || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Statut actuel: {getStatusLabel(sampleTracking.sample.statut)}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                Historique des Transitions
              </Typography>
              <List>
                {sampleTracking.timeline?.map((event: any, index: number) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <TimelineIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${getStatusLabel(event.from_status || 'DEBUT')} → ${getStatusLabel(event.to_status)}`}
                      secondary={formatDateTime(event.transition_date)}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        {isMobile && (
          <DialogActions>
            <Button onClick={() => setTrackingDialogOpen(false)}>Fermer</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientDashboard;
